import {
  MaintenanceBannerViewModel,
  MaintenanceState,
  MaintenanceStateInfo,
  maintenanceStateDisplayNames,
} from '@mp/system/maintenance/domain';

import { formatCountdown } from './format-countdown';
import { msUntilNextTransition } from './ms-until-next-transition';

/**
 * Builds the view model of the maintenance banner.
 *
 * Ob der Benutzer von der Sperre befreit ist, entscheidet allein der Server - gelesen wird es deshalb aus dem
 * Zustand und nicht aus einem zweiten Eingang, der davon abweichen könnte.
 * @param stateInfo The last known maintenance state, `null` while it has not been loaded.
 * @param correctedNow The current time (epoch milliseconds) corrected by the server time offset.
 * @returns The view model or `null` when no banner has to be rendered.
 */
export function buildMaintenanceBannerViewModel(
  stateInfo: MaintenanceStateInfo | null,
  correctedNow: number,
): MaintenanceBannerViewModel | null {
  if (!stateInfo) {
    return null;
  }

  if (stateInfo.state === MaintenanceState.Announced) {
    return {
      variant: 'announcement',
      title: maintenanceStateDisplayNames[MaintenanceState.Announced],
      icon: 'schedule',
      announcementText: stateInfo.announcementText,
      ...countdownOf(stateInfo, correctedNow, 'Beginn in'),
    };
  }

  // Während der Wartung sieht nur der befreite Benutzer einen Hinweis - alle anderen werden gesperrt.
  if (stateInfo.state === MaintenanceState.Active && stateInfo.isExemptForCurrentUser) {
    return {
      variant: 'activeStrip',
      title: maintenanceStateDisplayNames[MaintenanceState.Active],
      icon: 'build',
      // Der Streifen wiederholt den Ankündigungstext nicht: er stand vor dem Fenster im Ankündigungsbanner.
      announcementText: null,
      ...countdownOf(stateInfo, correctedNow, 'Noch'),
    };
  }

  return null;
}

/**
 * Der Countdown wird erst in den Zweigen gerechnet, die ihn anzeigen: für einen gesperrten Benutzer fällt
 * der Wert sonst jede Sekunde an und wird verworfen.
 */
function countdownOf(
  stateInfo: MaintenanceStateInfo,
  correctedNow: number,
  label: string,
): Pick<MaintenanceBannerViewModel, 'countdown' | 'countdownLabel'> {
  const countdown = formatCountdown(msUntilNextTransition(stateInfo, correctedNow));

  return { countdown, countdownLabel: countdown === null ? null : label };
}
